import * as React from 'react';
import { flow, forEach, map, get } from 'lodash';
import { II18nMessages } from '../../../../services/branding/models';
import { Alert, Button, Col, ControlLabel, Form, FormGroup, Row } from 'react-bootstrap';
import { Field, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import { FormattedNumber } from 'react-intl';
import FieldFormControl from '../../../../components/FieldFormControl';
import PostcodeControl from '../../../../components/PostcodeControl';
import {
  isAdult,
  isEmail,
  isNotFalse,
  isRequired,
  isValidDate,
  isPhone,
} from '../../../../util/validators';
import { IChannel, IPostcodeAddress, IPremium, IProduct, IQuotes } from '../../../../data/api/models';
import * as Commonmark from 'react-commonmark';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { FaSpinner } from 'react-icons/fa';
import { IQuoteState } from '../../../../services/quote/models';

interface IPaymentFormProps extends InjectedFormProps {
  i18n: II18nMessages;
  product: IProduct;
  quote: IQuoteState;
  quotes: IQuotes;
  onBack: () => void;
  premium: IPremium;

  renderDefinition(name: string): any;

  channel: IChannel;
}

class PaymentForm extends React.Component<IPaymentFormProps, any> {
  public state = {
    payOnCard: false,
    showCardDetails: true,
  };

  componentDidMount() {
    const { change } = this.props;
    change('reuseCustomerAddress', false);
  }

  private handleSelectAddress = (address: IPostcodeAddress) => {
    const { change } = this.props;
    const base = 'travellers[0].address';

    change(`${base}.line1`, address.line1);
    change(`${base}.line2`, address.line2);
    change(`${base}.town`, address.town);
    change(`${base}.county`, address.county);
    change(`${base}.postcode`, address.postcode);
  }

  private handleSelectCardHolderAddress = (address: IPostcodeAddress) => {
    const { change } = this.props;
    const base = 'card.billing.address';

    change(`${base}.line1`, address.line1);
    change(`${base}.line2`, address.line2);
    change(`${base}.town`, address.town);
    change(`${base}.county`, address.county);
    change(`${base}.postcode`, address.postcode);
  }

  private handleOnChange = (data, value) => {
    this.setState({showCardDetails: !value})
  }

  private renderPremium = () => {
    const { quotes, premium } = this.props;

    if (!quotes) {
      return null;
    }

    if (!premium) {
      return null;
    }

    return (
      <FormattedNumber
        value={premium.gross}
        style="currency"
        currency={premium.currency}
      />
    );
  }

  public render() {
    const { i18n, product, quote, submitting, handleSubmit, onBack, valid, submitFailed, channel } = this.props;

    const year = (new Date().getFullYear());
    const yearList = [];
    const monthList = [];

    for (let i = 0; i < 10; i++) {
      yearList.push({ label: year + i, value: year + i });
    }

    for (let i = 1; i <= 12; i++) {
      const num = i < 10 ? '0' + `${i}` : i;
      monthList.push({ label: num, value: num });
    }

    return (
      <Form horizontal={true} onSubmit={handleSubmit}>

        <div className="section">
          <h3>{i18n.quote.payment.subTitle}</h3>
          <p>{i18n.quote.payment.info1}</p>
          {product.metadata.definitions.your_details && (
            <p><strong>{product.metadata.definitions.your_details}</strong></p>
          )}
          <Row className="margin-top">
            <Col sm={12}>
              <Col sm={12} className="traveller">
                <h5>{i18n.quote.tripDetails.traveller.title}</h5>
                <Row>
                  <Col sm={12}>
                    <FormGroup controlId="name">
                      <Col componentClass={ControlLabel} sm={3}>
                        {i18n.quote.tripDetails.traveller.name}
                      </Col>
                      <Col md={2} sm={9}>
                        <Field
                          name="travellers[0].title"
                          type="text"
                          placeholder="Title"
                          component={FieldFormControl}
                        />
                      </Col>
                      <Col sm={3} mdHidden={true} lgHidden={true}/>
                      <Col md={4} sm={9}>
                        <Field
                          name="travellers[0].firstName"
                          type="text"
                          placeholder="First Name"
                          component={FieldFormControl}
                        />
                      </Col>
                      <Col sm={3} mdHidden={true} lgHidden={true}/>
                      <Col md={3} sm={9}>
                        <Field
                          name="travellers[0].lastName"
                          type="text"
                          placeholder="Last Name"
                          component={FieldFormControl}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup controlId="dateOfBirth">
                      <Col componentClass={ControlLabel} sm={3}>
                        {i18n.quote.tripDetails.traveller.dob}
                      </Col>
                      <Col md={6} sm={9}>
                        <Field
                          name="travellers[0].dob"
                          type="masked"
                          component={FieldFormControl}
                          mask="11/11/1111"
                          placeholder="dd/mm/yyyy"
                          className="form-control"
                          validate={[isRequired, isValidDate, isAdult]}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup controlId="postcode">
                      <Col componentClass={ControlLabel} sm={3}>
                        {i18n.quote.tripDetails.traveller.postcode}
                      </Col>
                      <Col md={6} sm={9}>
                        <Field
                          name="travellers[0].address.postcode"
                          type="text"
                          component={PostcodeControl}
                          onSelectAddress={this.handleSelectAddress}
                          validate={[
                            isRequired,
                          ]}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup controlId="address">
                      <Col componentClass={ControlLabel} sm={3}>
                        {i18n.quote.tripDetails.traveller.address}
                      </Col>
                      <Col md={6} sm={9}>
                        <Field
                          className="line-top"
                          name="travellers[0].address.line1"
                          placeholder="Line 1"
                          type="text"
                          component={FieldFormControl}
                          validate={[
                            isRequired,
                          ]}
                        />
                        <Field
                          className="line-middle"
                          name="travellers[0].address.line2"
                          placeholder="Line 2"
                          type="text"
                          component={FieldFormControl}
                        />
                        <Field
                          className="line-middle"
                          name="travellers[0].address.town"
                          type="text"
                          placeholder="Town"
                          component={FieldFormControl}
                        />
                        <Field
                          className="line-bottom"
                          name="travellers[0].address.county"
                          placeholder="County"
                          type="text"
                          component={FieldFormControl}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup controlId="phone">
                      <Col componentClass={ControlLabel} sm={3}>
                        {i18n.quote.tripDetails.traveller.phone}
                      </Col>
                      <Col md={6} sm={9}>
                        <Field
                          name="travellers[0].phone"
                          type="text"
                          component={FieldFormControl}
                          validate={[
                            isRequired,
                          ]}
                        />
                      </Col>
                    </FormGroup>
                  </Col>
                  <Col sm={12}>
                    <FormGroup controlId="email">
                      <Col componentClass={ControlLabel} sm={3}>
                        {i18n.quote.tripDetails.traveller.email}
                      </Col>
                      <Col md={6} sm={9}>
                        <Field
                          name="travellers[0].email"
                          type="text"
                          component={FieldFormControl}
                          validate={[
                            isRequired,
                            isEmail,
                          ]}
                        />
                      </Col>
                    </FormGroup>
                    {/*<Row>
                      <Col sm={3} xs={3} />
                      <Col md={6} sm={9}>
                        <div className="radio-horizontal text-me-details-field">
                          <Field
                            name="textMe"
                            type="checkbox"
                            value="single"
                            component={FieldFormControl}
                          >
                            <div />
                            {i18n.quote.payment.textMe}
                            <FaMobile/>
                          </Field>
                        </div>
                      </Col>
                    </Row>*/}
                  </Col>
                </Row>
              </Col>
            </Col>
          </Row>
        </div>

        <div className="section">
          <h3>{i18n.quote.payment.declaration}</h3>

          {map(product.metadata.purchase_declaration, this.renderCommonmark)}

          <Row className="radio-horizontal">
            <Col sm={12}>
              <Field
                name="purchaseDeclaration"
                type="checkbox"
                component={FieldFormControl}
                validate={[
                  isRequired,
                  isNotFalse,
                ]}
              >
                <div/>
                Please tick to confirm you agree to the above declaration.
              </Field>
            </Col>
          </Row>
        </div>

        <div className="white-container">
          <h4>Payment Details</h4>

          {quote.errorMessage && (
            <Alert bsStyle={'danger'} style={{ color: '#d64f48', marginBottom: 10 }}>
              {quote.errorMessage}
            </Alert>
          )}

          <div className="total-price-content">
            Total to pay:
            <span className="gross"> {this.renderPremium()}</span>
            <span className="include-ipt"> (inc. IPT)</span>
          </div>
          <div>
            <p>
              Please only click 'Purchase Cover' once. Do not refresh or click back in your browser
              once you have clicked 'Purchase Cover'. If you experience any issues, please call us on {channel.phone}.
            </p>

            <div className="section">
              <Row className="radio-horizontal">
                <Col sm={12}>
                  <Field
                    name="reuseCustomerAddress"
                    type="checkbox"
                    component={FieldFormControl}
                    onChange={this.handleOnChange}
                    validate={[
                      isRequired
                    ]}
                  >
                    <div/>
                    Are cardholder details same as yours?
                  </Field>
                </Col>
              </Row>
            </div>

            {this.state.showCardDetails && (
              <>
                <Row>
                  <Col sm={6}>
                    <Field
                      name="card.billing.name"
                      type="text"
                      placeholder="Cardholder name"
                      component={FieldFormControl}
                      validate={[isRequired]}
                      className="payment-field"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}>
                    <Field
                      name="card.billing.email"
                      type="text"
                      placeholder="Cardholder Email"
                      component={FieldFormControl}
                      validate={[isRequired, isEmail]}
                      className="payment-field"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}>
                    <Field
                      name="card.billing.phone"
                      type="text"
                      placeholder="Cardholder Phone"
                      component={FieldFormControl}
                      validate={[isRequired, isPhone]}
                      className="payment-field"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}>
                    <Field
                      name="card.billing.address.postcode"
                      type="text"
                      placeholder={i18n.quote.tripDetails.traveller.postcode}
                      component={PostcodeControl}
                      onSelectAddress={this.handleSelectCardHolderAddress}
                      validate={[
                        isRequired,
                      ]}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}>
                    <label>{i18n.quote.tripDetails.traveller.address}</label>
                  </Col>
                </Row>
                <Row>
                  <Col md={6} sm={6}>
                    <Field
                      className="line-top"
                      name="card.billing.address.line1"
                      placeholder="Line 1"
                      type="text"
                      component={FieldFormControl}
                      validate={[
                        isRequired,
                      ]}
                    />
                    <Field
                      className="line-middle"
                      name="card.billing.address.line2"
                      placeholder="Line 2"
                      type="text"
                      component={FieldFormControl}
                    />
                    <Field
                      className="line-middle"
                      name="card.billing.address.town"
                      type="text"
                      placeholder="Town"
                      component={FieldFormControl}
                    />
                    <Field
                      className="line-bottom payment-field"
                      name="card.billing.address.county"
                      placeholder="County"
                      type="text"
                      component={FieldFormControl}
                    />
                  </Col>
                </Row>
              </>
            )}

          </div>
        </div>

        <div className="btn-bar">
          <Button
            bsStyle="primary"
            type="button"
            onClick={onBack}
          >
            Back
          </Button>
          <div className="pull-right">
            <Button
              type="submit"
              bsStyle="primary"
              className="btn-next"
              disabled={submitting || !valid}
            >
              {submitting && (<FaSpinner className="fa-spin spin-icon"/>)}&nbsp;
              Purchase Cover
            </Button>
          </div>
        </div>
      </Form>
    );
  }

  private renderCommonmark = (value, key) => {
    return (
      <div key={key}>
        <Commonmark
          source={value}
          linkTarget="_blank"
          transformLinkUri={this.transformLinks}
        />
      </div>
    );
  }

  private transformLinks = (url) => {
    const { premium } = this.props;
    if (!premium) {
      return null;
    }

    if (url === 'policy_wording') {
      return premium.scheme.documents['Policy Wording'];
    }

    if (url === 'policy_summary') {
      return premium.scheme.documents['Policy Summary'];
    }

    if (url === 'tobca') {
      const tobca = get(premium.scheme.documents, 'Terms of Business Customer Agreement', null);
      const toba = get(premium.scheme.documents, 'Terms of Business Agreement', null);
      return tobca ? tobca : toba;
    }

    if (url === 'ipid') {
      return premium.scheme.documents['Insurance Product Information Document'];
    }

    return url;
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const channel = state.branding.channel;
      const quotes = state.quote.quotes;
      const quote = state.quote;
      const schemeId = selector(state, 'schemeId');

      let premium = null;

      forEach(quotes, (tripTypes, level) => {
        forEach(tripTypes, (quotedPremium) => {
          if (quotedPremium.scheme.id === schemeId) {
            premium = quotedPremium;
          }
        });
      });

      return {
        schemeId,
        premium,
        channel,
        quotes,
        quote,
      };
    },
  ),
])(PaymentForm);
